import moment from "moment";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
// import UserActivityStatus from "../../../../components/user-activity-status/user-activity-status";
import editIcon from "../../../../assets/edit.svg";
import "../styles.css";
import "../../../../components/profile/styles.css";
import { Button, Form, Modal } from "antd";
import { useForm } from "antd/es/form/Form";
import InviteContent from "../../../../components/modals/invitations/components/invite-content";
import EnrollmentForm from "../../../../components/modals/invitations/enrollment-form/enrollment-form";
import ApiEnrollmentScenarioManagement from "../../../../api/enrollment-scenario-management";
import axios from "axios";
import { useTranslation } from "react-i18next";
import useNotification from "../../../../components/notification/notification";
import ApiSubject from "../../../../api/subject";
import ApiSubjectsManagement from "../../../../api/subjects-manager";
import { Body } from "../../../../components/main-body/body";
import { useProjectNavigate } from "../../../../common/utils/useProjectNavigate";import ProfileCard from "../../../administration/profile/profile-card/profile-card";
import ApiProfileManagement from "../../../../api/profile-management";
import { useSelector } from "react-redux";
import { selectSettings } from "../../../../store/slices/settings";
import ApiSiteManagement from "../../../../api/site-managements";
import {
  selectIsSystemAdmin,
} from "../../../../store/slices/user";
import { useCan } from "../../../../common/utils/usePermissions";

const TIME_BASED_INPUT_TYPES = ["message:date", "message:time"];

const looksLikeIsoTimestamp = (v) =>
  typeof v === "string" && /^\d{4}-\d{2}-\d{2}T/.test(v);

const resolveFieldMeta = (field, scenarioData, profiles) => {
  let boundTo = field.boundTo?.[0];
  let inputType = field.inputType;

  if ((!boundTo || !inputType) && profiles?.length) {
    const definition = profiles.find(
      (p) => p.id === field.id || p.title === field.title
    );
    boundTo = boundTo || definition?.boundTo?.[0];
    inputType = inputType || definition?.inputType;
  }

  const sources = [
    ...(scenarioData?.scenarios || []),
    ...(scenarioData?.onboardings || []),
    ...(scenarioData?.dailySurveyTasks || []),
  ];
  const scenarioDataLoaded = sources.length > 0;

  const source = boundTo
    ? sources.find((s) => s.id === boundTo.sourceId)
    : null;
  const format = source?.data?.[boundTo?.nodeId]?.data?.format || null;

  return { format, inputType, scenarioDataLoaded };
};

const formatFieldValue = (field, scenarioData, profiles) => {
  if (field.value === null || field.value === undefined || field.value === "") {
    return "N/A";
  }

  const { format, inputType, scenarioDataLoaded } = resolveFieldMeta(
    field,
    scenarioData,
    profiles
  );

  const needsFormatting =
    TIME_BASED_INPUT_TYPES.includes(inputType) ||
    looksLikeIsoTimestamp(field.value);

  if (needsFormatting && !scenarioDataLoaded) {
    return "";
  }

  if (format) {
    const parsed = moment(field.value);
    return parsed.isValid() ? parsed.format(format) : field.value;
  }

  return field.value;
};

const DeleteModalComponent = ({
  item,
  handleOk,
  handleCancel,
  isModalOpen,
}) => {
  return (
    <Modal
      title={item.displayId}
      open={isModalOpen}
      onOk={() => {
        handleOk(item.id, item);
      }}
      onCancel={handleCancel}
      okButtonProps={{ danger: true, "data-testid": "subject-delete-confirm" }}
      okText="Delete"
      className="plan-page-action-modal"
    >
      <p>Are you sure you want to delete this subject?</p>
    </Modal>
  );
};

const Profile = ({ subject, fetchSubject }) => {
  const { t } = useTranslation();
  const [form] = useForm();
  const {
    invitedAt,
    onboardingStartedAt,
    onboardingCompletedAt,
    email,
    enrollmentData,
    weekIndex,
    artificial,
    timeZone,
  } = subject;
  const [isEdit, setIsEdit] = useState(false);
  const [selectsValues, setSelectsValues] = useState({});
  const [inputsValues, setInputsValues] = useState({});
  const [hasChanges, setHasChanges] = useState(false);
  const [isOpenDeleteModal, setOpenDeleteModal] = useState(false);
  const { openNotification } = useNotification();
  const navigate = useProjectNavigate();
  const [isOpen, setOpen] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState({});
  const [profiles, setProfiles] = useState([]);
  const [scenarioData, setScenarioData] = useState({});
  const settings = useSelector(selectSettings);
  const sitesEnabled =
    settings?.find((s) => s.key === "sitesEnable")?.value === true;
  const [sites, setSites] = useState([]);

  const isSystemAdmin = useSelector(selectIsSystemAdmin);
  // LF360-2024: the server checks the same permission on each of these.
  const canEdit = useCan("subjects.edit");
  const canChangeStatus = useCan("subjects.status");
  const canDelete = useCan("subjects.delete");
  const canSeeContacts = useCan("subjects.contacts");

  const [enrollmentTemplate, setEnrollmentTemplate] = useState([]);
  // Graph-aware enrollment editing (conditional logic on the fly).
  const [enrollmentGraph, setEnrollmentGraph] = useState(null);
  const [enrollmentFields, setEnrollmentFields] = useState([]);
  const [enrollmentState, setEnrollmentState] = useState({ complete: false, customFields: {} });
  const enrollmentBaselineRef = useRef(null);

  const getSites = async (query = "") => {
    return ApiSiteManagement.getSites({ query }).then(({ data }) => {
      setSites(
        (data?.sites || []).map((x) => ({
          id: x.id,
          label: x.name,
          value: x.id,
        }))
      );
    });
  };

  useEffect(() => {
    if (sitesEnabled) {
      getSites();
    }
  }, [sitesEnabled]);

  const openProfileDrawer = (field) => {
    const profile = profiles.find((p) => p.id === field.profileFieldId);
    setSelectedProfile(profile);
    setOpen(true);
  };

  const closeProfileDrawer = () => {
    setOpen(false);
    setSelectedProfile({});
  };

  const getProfiles = async (deselect, query = "") => {
    return ApiProfileManagement.getProfiles({ query }).then(({ data }) => {
      const { subjectProfileFields } = data || {};
      setProfiles(subjectProfileFields);
    });
  };

  const getScenarioData = () => {
    return ApiProfileManagement.getScenarioData().then((res) => {
      setScenarioData(res.data.scenarioData);
    });
  };

  useEffect(() => {
    getProfiles();
    getScenarioData();
  }, []);

  const onModalSelectChange = (id, value) => {
    setHasChanges(true);
    setSelectsValues((prev) => ({ ...prev, [id]: value }));
  };

  const mockWeeks = useMemo(
    () =>
      Array.from({ length: subject.weekCount }, (_, i) => ({
        label: `Week ${i + 1}`,
        value: i,
        default: subject.weekIndex === i,
      })),
    [subject.weekCount, subject.weekIndex]
  );

  // View mode fields
  const enrollmentDataFields = useMemo(() => {
    const baseFields = [
      ...[...enrollmentData]
        .sort((a, b) => a.sort - b.sort)
        .map((data) => (
          <div
            key={data.nodeId || data.label}
            className="profile_form-item profile_form-item--vertical"
            style={{ padding: "0px 24px " }}
          >
            <p>{data.label}</p>
            <span>{data.value || "—"}</span>
          </div>
        )),
      <div
        key="current-week"
        className="profile_form-item profile_form-item--vertical"
        style={{ padding: "0px 24px" }}
      >
        <p>Current week</p>
        <span>
          {subject.weekCount > 0
            ? subject.weekIndex !== undefined && subject.weekIndex >= 0
              ? `Week ${subject.weekIndex + 1}`
              : "No plan assigned"
            : "Week 1 — no plan assigned"}
        </span>
      </div>,
    ];

    if (sitesEnabled) {
      const siteName =
        subject.Site?.name ??
        sites.find((s) => s.value === subject.siteId)?.label;

      baseFields.push(
        <div
          key="site"
          className="profile_form-item profile_form-item--vertical"
          style={{ padding: "0px 24px" }}
        >
          <p>Site</p>
          <span>{siteName || "-"}</span>
        </div>
      );
    }

    baseFields.push(
      <div
        key="time-zone"
        className="profile_form-item profile_form-item--vertical"
        style={{ padding: "0px 24px" }}
      >
        <p>Time zone</p>
        <span>{timeZone || "UTC"}</span>
      </div>
    );

    return baseFields;
  }, [enrollmentData, sitesEnabled, subject, sites, timeZone]);

  const fetchEnrollInviteUserTemplate = () => {
    Promise.all([
      ApiEnrollmentScenarioManagement.getEnrollmentScenarioForm(),
      axios.get("api/v1/enrollment-fields/user"),
    ])
      .then(([scenarioRes, fieldsRes]) => {
        const fields = fieldsRes?.data?.enrollmentFields || [];
        setEnrollmentFields(fields);
        setEnrollmentGraph(scenarioRes?.data?.enrollmentScenario?.data || null);

        // Fallback flat template — used only when there's no enrollment-scenario
        // graph (the graph-aware EnrollmentForm is preferred otherwise).
        const byId = {};
        const byLabel = {};
        for (const f of fields) {
          byId[f.id] = f;
          byLabel[f.label] = f;
        }
        const tpl = (enrollmentData || []).map((data) => {
          const meta = byId[data.enrollmentFieldId] || byLabel[data.label] || {};
          return {
            ...meta,
            id: data.enrollmentFieldId || meta.id,
            label: data.label,
            type: data.type || meta.type,
            enrollmentResponseId: data.enrollmentResponseId,
            defaultValue:
              data.value !== "" && data.value != null ? data.value : undefined,
          };
        });
        setEnrollmentTemplate(tpl);
      })
      .catch(() => {});
  };

  useEffect(() => {
    fetchEnrollInviteUserTemplate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subject]);

  // Prefill the graph-aware editor with the subject's saved answers, keyed by
  // graph nodeId (the shape EnrollmentForm expects).
  const initialAnswers = useMemo(() => {
    if (!enrollmentGraph) {
      return {};
    }
    const byNode = {};
    for (const r of subject.enrollmentResponses || []) {
      const field = r.EnrollmentField;
      if (!field || !field.nodeId) {
        continue;
      }
      const node = enrollmentGraph[field.nodeId];
      const nodeType = node && node.data && node.data.nodeType;
      if (nodeType === "message:multi") {
        if (!byNode[field.nodeId]) {
          byNode[field.nodeId] = { answers: [] };
        }
        if (r.enrollmentFieldOptionId) {
          byNode[field.nodeId].answers.push({
            value: r.enrollmentFieldOptionId,
            label: r.EnrollmentFieldOption && r.EnrollmentFieldOption.value,
          });
        }
      } else if (nodeType === "message:single") {
        byNode[field.nodeId] = {
          value: r.enrollmentFieldOptionId,
          label: r.EnrollmentFieldOption && r.EnrollmentFieldOption.value,
        };
      } else {
        byNode[field.nodeId] = r.value;
      }
    }
    // The email node is hidden in the profile but must be marked answered so the
    // walk proceeds and "complete" can be reached.
    for (const [nid, node] of Object.entries(enrollmentGraph)) {
      if (node && node.data && node.data.alias === "email") {
        byNode[nid] = subject.email || "";
      }
    }
    return byNode;
  }, [subject, enrollmentGraph]);

  // EnrollmentField id -> the subject's existing response id (so edits update the
  // existing row instead of creating a duplicate).
  const responseIdByFieldId = useMemo(() => {
    const map = {};
    for (const r of subject.enrollmentResponses || []) {
      if (!map[r.enrollmentFieldId]) {
        map[r.enrollmentFieldId] = r.id;
      }
    }
    return map;
  }, [subject]);

  const onEnrollmentChange = useCallback((state) => {
    setEnrollmentState(state);
    const snap = JSON.stringify(state.customFields || {});
    if (enrollmentBaselineRef.current === null) {
      enrollmentBaselineRef.current = snap; // first report = baseline (prefilled)
    } else if (snap !== enrollmentBaselineRef.current) {
      setHasChanges(true);
    }
  }, []);

  const contentFieldsMemo = useMemo(() => {
    const fields = [...enrollmentTemplate];

    // When the subject's cohort isn't linked to a plan there are no weeks to pick
    // (weekCount === 0). Show a disabled, read-only field explaining the state
    // instead of an empty/unusable dropdown — setting a week would do nothing
    // without a plan anyway.
    if (subject.weekCount > 0) {
      fields.push({
        label: "Current week",
        type: "select",
        options: mockWeeks,
        defaultValue: weekIndex >= 0 ? weekIndex : undefined,
        id: "weekIndex",
      });
    } else {
      fields.push({
        label: "Current week",
        type: "select",
        options: [],
        disabled: true,
        defaultValue: "Week 1 — no plan assigned",
        id: "weekIndex",
      });
    }

    // LF360-2024: whoever edits the subject; the server holds a caller
    // limited to their sites to them.
    if (sitesEnabled) {
      fields.push({
        id: "siteId",
        label: "Site",
        type: "select",
        options: sites,
        defaultValue: subject.siteId ?? null,
      });
    }

    return fields;
  }, [
    enrollmentTemplate,
    mockWeeks,
    weekIndex,
    sitesEnabled,
    sites,
    subject.siteId,
    subject.weekCount,
  ]);

  // Non-enrollment extras (week / site) rendered alongside the graph-aware
  // enrollment editor.
  const extraFieldsMemo = useMemo(() => {
    const fields = [];
    if (subject.weekCount > 0) {
      fields.push({
        label: "Current week",
        type: "select",
        options: mockWeeks,
        defaultValue: weekIndex >= 0 ? weekIndex : undefined,
        id: "weekIndex",
      });
    } else {
      fields.push({
        label: "Current week",
        type: "select",
        options: [],
        disabled: true,
        defaultValue: "Week 1 — no plan assigned",
        id: "weekIndex",
      });
    }
    // LF360-2024: whoever edits the subject; the server holds a caller
    // limited to their sites to them.
    if (sitesEnabled) {
      fields.push({
        id: "siteId",
        label: "Site",
        type: "select",
        options: sites,
        defaultValue: subject.siteId ?? null,
      });
    }
    return fields;
  }, [
    mockWeeks,
    weekIndex,
    sitesEnabled,
    sites,
    subject.siteId,
    subject.weekCount,
  ]);

  const toogleEdit = () => {
    if (isEdit) {
      setInputsValues({});
      setSelectsValues({});
    }
    // Reset graph-aware editor tracking for a fresh edit session.
    enrollmentBaselineRef.current = null;
    setEnrollmentState({ complete: false, customFields: {} });
    setIsEdit(!isEdit);
  };

  const isDisabledUser = () => ["blocked", "inactive"].includes(subject.status);

  const isNotApprovedSubject = () =>
    ["invited", "inviteError", "onboarding"].includes(subject.status);

  const reinvite = () => {
    ApiSubject.reinvite(subject.id)
      .then(() => {
        openNotification({
          type: "success",
          message: (
            <p>{t("profile.notification.reinvite.success", { email })}</p>
          ),
        });
      })
      .catch(() => {
        openNotification({
          type: "error",
          message: <p>{t("profile.notification.reinvite.error")}</p>,
        });
      });
  };

 const copyInvitationLink = async () => {
    try {
      const { data } = await ApiSubject.getInvitationLink(subject.id);
      const invitationLink = data?.invitationLink;

      if (!invitationLink) {
        throw new Error("Invitation link not found");
      }

      await navigator.clipboard.writeText(invitationLink);

      openNotification({
        type: "success",
        message: <p>Invitation link copied</p>,
      });
    } catch (e) {
      openNotification({
        type: "error",
        message: <p>Failed to copy invitation link</p>,
      });
    }
  };

  const toogleUserStatus = () => {
    const { activate, deactivate } = ApiSubject;
    const disabledNow = isDisabledUser();
    const toogle = disabledNow ? activate(subject.id) : deactivate(subject.id);
    toogle
      .then(() => {
        openNotification({
          type: "success",
          message: (
            <p>
              {t(
                `profile.notification.toogle_subject.${
                  disabledNow ? "activated" : "deactivated"
                }`,
                { name: subject.displayId }
              )}
            </p>
          ),
        });
      })
      .catch(() => {
        openNotification({
          type: "error",
          message: <p>{t("profile.notification.toogle_subject.error")}</p>,
        });
      })
      .finally(() => {
        fetchSubject();
      });
  };

  const handleFormChange = (data) => {
    setHasChanges(true);
    const { value, name } = data[0];
    setInputsValues((prev) => ({ ...prev, [name[0]]: value }));
  };

  const updateSubject = () => {
    // Extras (week / site) come from the InviteContent form; in the graph-aware
    // path that's all it holds, in the fallback path it also holds enrollment.
    const extras = { ...inputsValues, ...selectsValues };
    if (extras.weekIndex != null) extras.weekIndex = Number(extras.weekIndex);
    if (extras.siteId != null && extras.siteId !== "") {
      extras.siteId = String(extras.siteId);
    }

    // Graph-aware enrollment answers (keyed by EnrollmentField id) → map to the
    // existing response id when present (update) or keep the field id (create).
    const enrollmentPayload = {};
    if (enrollmentGraph) {
      const cf = enrollmentState.customFields || {};
      for (const [fieldId, value] of Object.entries(cf)) {
        const key = responseIdByFieldId[fieldId] || fieldId;
        enrollmentPayload[key] = value;
      }
    }

    const payload = { subjectId: subject.id, ...enrollmentPayload, ...extras };

    ApiSubjectsManagement.updateSubjectData(payload)
      .then(() => {
        toogleEdit();
        setHasChanges(false);
        fetchSubject();
      })
      .then(() => {
        openNotification({
          type: "success",
          message: (
            <p>
              {t("subject_management.notification.profile.updated", {
                subject: subject.displayId,
              })}
            </p>
          ),
        });
      })
      .catch((e) => {
        const code = e?.response?.data?.error?.code;
        const missing = e?.response?.data?.error?.missing;
        openNotification({
          type: "error",
          message: (
            <p>
              {code === "MISSING_ENROLLMENT_FIELDS"
                ? `Please fill all enrollment fields before saving${
                    missing && missing.length
                      ? `: ${missing.map((m) => m.label).join(", ")}`
                      : ""
                  }.`
                : t([
                  `subject_management.notification.profile.${code}`,
                  "subject_management.notification.profile.error",
                ])}
            </p>
          ),
        });
      });
  };

  const openSubjectDeleteModal = () => setOpenDeleteModal(true);
  const closeSubjectDeleteModal = () => setOpenDeleteModal(false);

  const deleteSubject = (id) => {
    ApiSubjectsManagement.deleteSubject(id)
      .then(() => {
        openNotification({
          type: "success",
          message: <p>{t("profile.notification.delete_subject.success")}</p>,
        });
        navigate("/subject-management#subjects");
      })
      .catch(() => {
        openNotification({
          type: "error",
          message: <p>{t("profile.notification.delete_subject.error")}</p>,
        });
      });
  };

  const disabledUser = isDisabledUser();
  const notApprovedSubject = isNotApprovedSubject();
  const isDeleteDisabled =
    String(process.env.DISABLED_DELETE_SUBJECT).toLowerCase() === "true";

  const headerConfig = useMemo(() => {
    if (!canChangeStatus && !canDelete) {
      return { title: "Profile data" };
    }

    const header = {
      title: "Profile data",
    };

    header.apply = [];

    // Activate / Deactivate
    if (canChangeStatus) {
      header.apply.push({
        label: t(
          `profile.form.${
            disabledUser ? "activate_subject" : "deactivate_subject"
          }`
        ),
        onClick: toogleUserStatus,
        type: "default",
        testID: "subject-toggle-status",
      });
    }

    // Reinvite + Copy link
    if (canChangeStatus && notApprovedSubject) {
      header.apply.push(
        {
          label: t("profile.form.reinvite_subject"),
          onClick: reinvite,
          type: "default",
        },
        {
          label: "Copy invitation link",
          onClick: copyInvitationLink,
          type: "default",
        }
      );
    }

    // Delete (only when disabled and not forbidden by env)
    if (canDelete && disabledUser && !isDeleteDisabled) {
      header.cancel = {
        label: t("profile.form.delete_subject"),
        onClick: openSubjectDeleteModal,
        type: "default",
        testID: "subject-delete",
      };
    }

    return header;
  }, [
    canChangeStatus,
    canDelete,
    disabledUser,
    notApprovedSubject,
    isDeleteDisabled,
    t,
    toogleUserStatus,
    reinvite,
    openSubjectDeleteModal,
  ]);

  return (
    <Body header={headerConfig} scroll>
      <div className="profile_form-columns">
        <div className="profile_form-container profile_form-column-left" data-testid="subject-profile">
          <div className="profile_form__header">
            <span className="profile_form__header__title">Subject profile</span>
            <div>
              {canEdit &&
                (isEdit ? (
                  <div className="profile-header__btns">
                    <Button
                      style={{
                        height: 40,
                        background: "rgba(108, 108, 137, 0.08)",
                      }}
                      onClick={toogleEdit}
                    >
                      {t("profile.btns.cancel")}
                    </Button>
                    <Button
                      type="primary"
                      style={{ marginLeft: 8, height: 40 }}
                      onClick={updateSubject}
                      disabled={!hasChanges || (!!enrollmentGraph && !enrollmentState.complete)}
                      data-testid="profile-save"
                    >
                      {t("profile.btns.save")}
                    </Button>
                  </div>
                ) : (
                  <img
                    src={editIcon}
                    alt="edit"
                    role="presentation"
                    onClick={toogleEdit}
                    data-testid="profile-edit"
                  />
                ))}
            </div>
          </div>

          {canSeeContacts && (
            <div
              className="profile_form-item profile_form-item--vertical"
              style={{ padding: "0px 24px ", fontSize: "14px" }}
            >
              <p>Email</p>
              <span>{email}</span>
            </div>
          )}

          <Form form={form} onFieldsChange={handleFormChange}>
            {isEdit ? (
              enrollmentGraph && Object.keys(enrollmentGraph).length ? (
                <>
                  {/* Graph-aware enrollment editor — conditional logic (if/else,
                      goto) is resolved on the fly as answers change. */}
                  <EnrollmentForm
                    key={`enroll-${subject.id}`}
                    graph={enrollmentGraph}
                    fields={enrollmentFields}
                    initialAnswers={initialAnswers}
                    hiddenAliases={["email"]}
                    onChange={onEnrollmentChange}
                    borderless
                    fieldClassName="invite-user__field invite-user__field--vertical"
                  />
                  <InviteContent
                    key={`extras-${sites.length}`}
                    fields={extraFieldsMemo}
                    onSelectChange={onModalSelectChange}
                    vertical={true}
                  />
                </>
              ) : (
                <InviteContent
                  key={`fields-${sites.length}`} // fallback: no enrollment-scenario graph
                  fields={contentFieldsMemo}
                  onSelectChange={onModalSelectChange}
                  vertical={true}
                />
              )
            ) : (
              enrollmentDataFields
            )}
          </Form>

          {/* LF360-1913. "Registration date" used to show the invitation, which
              is the admin's doing rather than the subject's. It keeps its own
              name now, and the two dates that belong to the subject sit beside
              it. A subject who has not reached one of them shows a dash — an
              onboarding that has not happened has no date to give. */}
          {[
            { label: "Invitation sent", value: invitedAt },
            { label: "Registration date", value: onboardingStartedAt },
            { label: "Onboarding completed", value: onboardingCompletedAt },
          ].map(({ label, value }) => (
            <div
              key={label}
              className="profile_form-item profile_form-item--vertical"
              style={{ padding: "0px 24px ", fontSize: "14px" }}
            >
              <p>{label}</p>
              <span>{value ? moment(value).format("DD MMM YYYY") : "—"}</span>
            </div>
          ))}

          {isSystemAdmin && (
            <div
              className="profile_form-item profile_form-item--vertical"
              style={{ padding: "0px 24px ", fontSize: "14px" }}
            >
              <p>Artificial</p>
              <span>{artificial ? "Yes" : "No"}</span>
            </div>
          )}
        </div>

        <div className="profile_form-container profile_form-column-right">
          <div className="profile_form__header">
            <span className="profile_form__header__title">Custom fields</span>
          </div>
          {subject.customFields?.map((field) => (
            <div
              key={field.title}
              className="profile_form-item profile_form-item--hoverable"
              style={{ padding: "10px 19px ", fontSize: "14px" }}
              onClick={() => openProfileDrawer(field)}
            >
              <p>{field.title}</p>
              <span>{formatFieldValue(field, scenarioData, profiles)}</span>
            </div>
          ))}
        </div>
      </div>

      {canDelete && (
        <DeleteModalComponent
          isModalOpen={isOpenDeleteModal}
          handleCancel={closeSubjectDeleteModal}
          handleOk={deleteSubject}
          item={subject}
        />
      )}

      <ProfileCard
        profile={selectedProfile}
        isOpen={isOpen}
        close={closeProfileDrawer}
        scenarioData={scenarioData}
        onlyView={true}
        subjectId={subject.id}
      />
    </Body>
  );
};

export default Profile;
